import { AccountRoutes } from "../../constants/myAccount/routes";
import {
  DEFAULT_ORDER_STATUS_TEXT_COLOR,
  findOrderStatusConfig,
  ORDER_STATUS,
  ORDER_STATUS_BRAND_COLOR,
} from "../../constants/myAccount/orderStatus";
import { dateAndTimeFormatWithoutDay } from "./dateTime";

/**
 * @param {object} myOrdersPayload
 * @returns {object|undefined}
 */
export function getOrderDetailsFromMyOrders(myOrdersPayload) {
  return myOrdersPayload?.OrderDetails ?? myOrdersPayload?.orderDetails ?? myOrdersPayload;
}

/**
 * @param {object} orderDetails
 * @param {object} orderRow
 * @returns {string}
 */
export function resolveSubOrderDisplayStatus(orderDetails, orderRow) {
  const subOrderDetails = orderRow?.subOrderDetails;
  const orderId = subOrderDetails?.orderId;
  const expiredOrders = orderDetails?.data?.expiredOrders ?? [];
  if (orderId && expiredOrders.includes(orderId)) {
    return ORDER_STATUS.EXPIRED.label;
  }
  if (orderRow?.refundStatus) {
    return ORDER_STATUS.REFUNDED.label;
  }
  return subOrderDetails?.subOrderStatus ?? "";
}

/**
 * @param {object} orderDetails
 * @param {object} subOrderDetails
 * @returns {boolean}
 */
export function isRetryPaymentEnabled(orderDetails, subOrderDetails) {
  const orderId = subOrderDetails?.orderId;
  const expiredOrders = orderDetails?.data?.expiredOrders ?? [];
  const isExpired = orderId && expiredOrders.includes(orderId);
  return !isExpired && subOrderDetails?.subOrderStatus === ORDER_STATUS.WAITING.label;
}

/**
 * @param {string} statusLabel
 * @returns {string}
 */
export function getStatusColor(statusLabel) {
  const match = findOrderStatusConfig(statusLabel);
  return match?.backgroundColor ?? match?.color ?? ORDER_STATUS_BRAND_COLOR;
}

/**
 * @param {string} statusLabel
 * @returns {string}
 */
export function getStatusTextColor(statusLabel) {
  const match = findOrderStatusConfig(statusLabel);
  return match?.textColor ?? DEFAULT_ORDER_STATUS_TEXT_COLOR;
}

/**
 * Status pill styles from Figma: light tags when `backgroundColor` is set; solid fill + white text otherwise.
 * @param {string} statusLabel
 * @returns {{ className: string, style?: { backgroundColor: string, color?: string } }}
 */
export function getOrderStatusTagPresentation(statusLabel) {
  const match = findOrderStatusConfig(statusLabel);

  if (match?.backgroundColor) {
    return {
      className: "",
      style: {
        backgroundColor: match.backgroundColor,
        color: match.textColor ?? DEFAULT_ORDER_STATUS_TEXT_COLOR,
      },
    };
  }

  return {
    className: "text-white-900",
    style: { backgroundColor: match?.color ?? ORDER_STATUS_BRAND_COLOR },
  };
}

/**
 * @param {string} subOrderId
 * @param {string} [mode]
 * @returns {string}
 */
export function buildAccountViewDetailsPath(subOrderId, mode = AccountRoutes.VIEW_DETAILS) {
  if (!subOrderId) {
    return `/account/${AccountRoutes.MY_ORDERS}`;
  }
  return `/account/${mode}/${subOrderId}`;
}

/**
 * @param {string} [orderDateStr]
 * @returns {string}
 */
/**
 * @param {object} recipientDetails
 * @returns {string[]}
 */
export function buildRecipientAddressLines(recipientDetails) {
  if (!recipientDetails) {
    return [];
  }
  const lines = [];
  const showAddress1 =
    recipientDetails.recipientAddress1 && !String(recipientDetails.recipientAddress1).includes("_NA_");
  const showAddress2Domestic =
    recipientDetails.recipientAddress2 &&
    recipientDetails.recipientCountryGeoId === "IND" &&
    !String(recipientDetails.recipientAddress2).includes("_NA_");
  const streetParts = [
    recipientDetails.houseNo,
    showAddress1 ? recipientDetails.recipientAddress1 : "",
    showAddress2Domestic ? recipientDetails.recipientAddress2 : "",
    recipientDetails.landmark,
  ].filter(Boolean);
  if (streetParts.length) {
    lines.push(streetParts.join(", "));
  }
  const cityLine = [recipientDetails.recipientCity, recipientDetails.recipientPostalCode].filter(Boolean).join(" - ");
  if (cityLine) {
    lines.push(cityLine);
  }
  return lines;
}

/**
 * @param {object} orderRow
 * @param {object} subOrderDetails
 * @param {string} status
 * @returns {string}
 */
const ORDER_STATUS_DATE_HIDDEN = [
  ORDER_STATUS.HOLD.label,
  ORDER_STATUS.CANCELLED.label,
  ORDER_STATUS.RETURNED.label,
  ORDER_STATUS.WAITING.label,
  ORDER_STATUS.EXPIRED.label,
  ORDER_STATUS.LOST_IN_TRANSIT.label,
];

/**
 * @param {object} orderRow
 * @param {object} subOrderDetails
 * @param {string} status
 * @returns {string}
 */
export function formatOrderStatusDate(orderRow, subOrderDetails, status) {
  if (!subOrderDetails || ORDER_STATUS_DATE_HIDDEN.includes(status)) {
    return "";
  }

  const deliveryDetails = subOrderDetails.deliveryDetails ?? {};
  const deliveredDate = subOrderDetails.deliveredDate;
  const statusDateMap = {
    [ORDER_STATUS.CONFIRMED.label]: subOrderDetails.orderDateStr,
    [ORDER_STATUS.GIFTING_PARTNER_ASSIGNED.label]: subOrderDetails.GiftingPartnerAssignedDate,
    [ORDER_STATUS.PROCESSING.label]: subOrderDetails.processingStatusDate,
    [ORDER_STATUS.SHIPPED.label]: subOrderDetails.OutForDeliveryStatusDate,
    [ORDER_STATUS.READY_TO_BE_SHIPPED.label]: deliveredDate,
    [ORDER_STATUS.DISPATCHED.label]: deliveredDate,
    [ORDER_STATUS.DELIVERED.label]: deliveredDate,
    [ORDER_STATUS.DELIVERED_ON.label]: deliveredDate,
    [ORDER_STATUS.DELIVERY_ATTEMPTED.label]: deliveredDate,
    [ORDER_STATUS.REFUNDED.label]: orderRow?.refundDate,
  };

  const raw =
    statusDateMap[status] || deliveryDetails.estimatedDeliveryDateStr || deliveredDate || subOrderDetails.orderDateStr;

  return raw ? dateAndTimeFormatWithoutDay(raw) : "";
}

/**
 *
 * @param {*} orderDateStr
 * @returns
 */
export function formatOrderDateLabel(orderDateStr) {
  if (!orderDateStr) {
    return "";
  }
  const parsed = new Date(orderDateStr);
  if (Number.isNaN(parsed.getTime())) {
    return orderDateStr;
  }
  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * @param {string} [orderDateStr]
 * @returns {string}
 */
export function formatOrderPlacedOnLabel(orderDateStr) {
  if (!orderDateStr) {
    return "";
  }
  const raw = String(orderDateStr);
  const normalized = raw.includes(" ") && !raw.includes("T") ? raw.replace(" ", "T") : raw;
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }
  const day = parsed.getDate();
  const month = parsed.toLocaleString("en-GB", { month: "short" });
  const hours24 = parsed.getHours();
  const minutes = String(parsed.getMinutes()).padStart(2, "0");
  const suffix = hours24 >= 12 ? "PM" : "AM";
  const hours = hours24 % 12 || 12;
  return `${day} ${month}, ${hours}:${minutes} ${suffix}`;
}
